import axios from 'axios'
import { CacheStorageService } from '../services'

class CatalogService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl
    this.activeRequestController = null
    this.cacheStorageService = new CacheStorageService()
  }

  /**
   * @author Brayan Salas
   * @description Get the catalog to insert in new (publicity/offer/survey)
   * @date 28-06-2024
   * @param {String} token
   * @param {String} type
   * @memberof CatalogService
   */
  async getCatalogInsert(token, type) {
    let response = await axios.get(`${this.apiUrl}/catalog-insert?type=${type}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    return response.data.data
  }

  /**
    * @author Junaid Khan
    * @description Get the onboarding Resources
    * @date 21-06-2024
    * @param {String} token
    * @returns {Object}
    * @memberof CatalogService
    */
  async getOnboardingResources(token) {
    let response = await axios.get(`${this.apiUrl}/onboarding-resources-passthrough`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }

    return response.data.data
  }


  async getGeojsonData(token, bounds) {
    if (!bounds) return;
    // Abort previous request if it's still active
    if (this.activeRequestController) {
      this.activeRequestController.abort();
    }

    this.activeRequestController = new AbortController();
    const controller = this.activeRequestController;

    try {
      const response = await axios.get(`${this.apiUrl}/geojson?bounds=${JSON.stringify(bounds)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });

      if (response.data.statusCode !== 200) {
        throw new Error(response.data.message);
      }

      return response.data.data;
    } catch (_) {
    }
  }

  /**
   * @author Brayan Salas
   * @description Get the catalogs to insert/update client information
   * @date 12-07-2024
   * @param {String} token
   * @return {Object} 
   * @memberof MarketNicheService
   */
  async getCatalogClientInformation(token) {
    let response = await axios.get(`${this.apiUrl}/catalog-client-information`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });
    return response.data.data;
  }

  /**
   * @author Brayan Salas
   * @description Get catalog states by geonamesId
   * @date 06-11-2024
   * @param {String} token
   * @return {Object} 
   * @memberof CatalogService
   */
  async getCatalogStatesByGeonameId(geonameId, token) {
    const response = await this.cacheStorageService.fetchData(`${this.apiUrl}/onboarding-get-states?geonameId=${geonameId}`, token)

    return response.data.data;
  }

  /**
   * @author Brayan Salas
   * @description Get geonameId by countryName
   * @date 06-11-2024
   * @param {String} countryName
   * @param {String} token
   * @return {Object} 
   * @memberof CatalogService
   */
  async getCountryGeonameId(countryName, token) {
    countryName = countryName.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    const response = await this.cacheStorageService.fetchData(`${this.apiUrl}/onboarding-get-geoname-id?countryName=${countryName}`, token)

    return response.data.data;
  }

  /**
   * @author Brayan Salas
   * @description Get market niches by economic sector id
   * @date 12-11-2024
   * @param {String} countryName
   * @param {String} token
   * @return {Object} 
   * @memberof CatalogService
   */
  async getMarketNicheByEconomicSector(economicSectorId, token) {
    const response = await this.cacheStorageService.fetchData(`${this.apiUrl}/market-niches-atix-landing?economicSectorId=${economicSectorId}`, token)

    return response.data.data;
  }
}
export { CatalogService as default }
